import axios from 'axios';

// Constants
const GOOGLE_API_ENDPOINT = `${process.env.REACT_APP_API_URL}/api/google-calendar`;

// Types
export interface GoogleCalendarEvent {
  id?: string;
  summary: string;
  description?: string;
  location?: string;
  startDateTime: string;
  endDateTime: string;
  timeZone: string;
  allDay?: boolean;
  recurringEventId?: string;
}

// Main service functions
export const googleCalendarService = {
  // Initialize Google Calendar authentication
  initiateAuth: async () => {
    try {
      const response = await axios.get(`${GOOGLE_API_ENDPOINT}/auth-url`, {
         headers: {
            'Content-Type': 'application/json',
         },
      });
      return response.data.authUrl;
    } catch (error) {
      console.error('Error initiating Google Calendar auth:', error);
      throw error;
    }
  },

  // Check if user is authenticated with Google Calendar
  checkAuthStatus: async () => {
    try {
      const response = await axios.get(`${GOOGLE_API_ENDPOINT}/auth-status`, {
         headers: {
            'Content-Type': 'application/json',
         },
      });
      return response.data.isAuthenticated;
    } catch (error) {
      console.error('Error checking Google Calendar auth status:', error);
      return false;
    }
  },

  // Add a single event to Google Calendar
  addEventToGoogleCalendar: async (event: GoogleCalendarEvent) => {
    try {
      const response = await axios.post(`${GOOGLE_API_ENDPOINT}/add-event`, event, {
         headers: {
            'Content-Type': 'application/json',
         },
      });
      return response.data;
    } catch (error) {
      console.error('Error adding event to Google Calendar:', error);
      throw error;
    }
  },

  // Sync multiple events to Google Calendar
  syncEventsToGoogleCalendar: async (events: GoogleCalendarEvent[]) => {
    try {
      const response = await axios.post(
         `${GOOGLE_API_ENDPOINT}/sync-events`,
         { events },
         {
            headers: {
               'Content-Type': 'application/json',
            },
         }
      );
      return response.data;
    } catch (error) {
      console.error('Error syncing events to Google Calendar:', error);
      throw error;
    }
  },

  // Create a new calendar for organization events
  createCalendar: async (calendarName: string) => {
    try {
      const response = await axios.post(
         `${GOOGLE_API_ENDPOINT}/create-calendar`,
         { name: calendarName },
         {
            headers: {
               'Content-Type': 'application/json',
            },
         }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating Google Calendar:', error);
      throw error;
    }
  },

  // Disconnect Google Calendar
  disconnect: async () => {
    try {
      await axios.post(`${GOOGLE_API_ENDPOINT}/disconnect`, {
         headers: {
            'Content-Type': 'application/json',
         },
      });
      return true;
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      return false;
    }
  },
};
